/**
 * Categories.ts — single source of truth for POI category definitions (DUP-12).
 *
 * Before this file existed, KNOWN_CATEGORIES was copy-pasted in 6 files
 * (SystemsPage, POILayerControls, POILayerManager, GEOTIFFViewer, WebIfcViewer,
 * AutodeskViewer) together with the generateColorFromString / getIconForCategory /
 * getCategoryIcon / getCategoryColor helpers. All copies agreed on every icon and
 * color; canonical descriptions below are the full SystemsPage/POILayerControls text.
 *
 * Consumer-specific deviations that were preserved AT THE CONSUMER (do not fold
 * them in here without checking those files):
 * - POILayerManager keeps a REDUCED keyword fallback (no 'display'/'intercom'/'table').
 * - AutodeskViewer adds an 'Unassigned' entry and falls back to plain '📦'
 *   without keyword matching.
 * - RVTExtractorService keeps its own extraction-time CATEGORY_ICONS / color
 *   palette (several icons intentionally differ; values are stored in POI data).
 * - IFCAnalysisService 'Electrical' analysis color is '#F5A623' (not '#FFB800').
 */

export interface CategoryDefinition {
  icon: string;
  color: string;
  description: string;
}

// Canonical category definitions - includes both IFC categories and common Revit categories
export const CATEGORY_DEFINITIONS: Record<string, CategoryDefinition> = {
  // IFC-based categories
  'Structure': { icon: '🏗️', color: '#E94B3C', description: 'Structural elements: walls, beams, columns, slabs' },
  'Architecture': { icon: '🚪', color: '#F5A623', description: 'Architectural elements: doors, windows, stairs' },
  'HVAC': { icon: '🌡️', color: '#4A90E2', description: 'HVAC systems: ducts, air terminals, boilers' },
  'Electrical': { icon: '⚡', color: '#FFB800', description: 'Electrical systems: cables, lighting, switches' },
  'MEP': { icon: '⚡', color: '#FFB800', description: 'Electrical/MEP systems: distribution panels, fixtures' },
  'Plumbing': { icon: '🚰', color: '#50E3C2', description: 'Plumbing systems: pipes, fixtures, valves' },
  'Fire Protection': { icon: '🔥', color: '#D0021B', description: 'Fire safety: sprinklers, alarms' },
  'Equipment': { icon: '⚙️', color: '#7B68EE', description: 'Mechanical equipment: sensors, controllers' },
  'Furniture': { icon: '🪑', color: '#6B5B95', description: 'Furniture and furnishing elements' },
  'Spatial': { icon: '📐', color: '#88B04B', description: 'Spatial elements: rooms, spaces' },
  'Other': { icon: '📦', color: '#9B9B9B', description: 'Other building elements' },

  // Revit-specific categories (Audio/Visual, Communications, etc.)
  'Audio Visual Devices': { icon: '📺', color: '#9C27B0', description: 'TVs, projectors, screens, displays' },
  'Communication Devices': { icon: '📡', color: '#00BCD4', description: 'Phones, intercoms, networking equipment' },
  'Data Devices': { icon: '🖥️', color: '#607D8B', description: 'Network equipment, data ports' },
  'Electrical Equipment': { icon: '⚡', color: '#FFB800', description: 'Distribution boards, panels, transformers' },
  'Electrical Fixtures': { icon: '💡', color: '#FFC107', description: 'Light fixtures, outlets, switches' },
  'Lighting Fixtures': { icon: '💡', color: '#FFEB3B', description: 'Ceiling lights, lamps, LEDs' },
  'Fire Alarm Devices': { icon: '🚨', color: '#F44336', description: 'Smoke detectors, alarms, pull stations' },
  'Security Devices': { icon: '🔐', color: '#795548', description: 'Cameras, access control, sensors' },
  'Plumbing Fixtures': { icon: '🚿', color: '#00BCD4', description: 'Sinks, toilets, faucets, showers' },
  'Mechanical Equipment': { icon: '⚙️', color: '#607D8B', description: 'Pumps, motors, compressors' },
  'Air Terminals': { icon: '🌬️', color: '#4A90E2', description: 'Diffusers, grilles, registers' },
  'Duct Accessories': { icon: '🔧', color: '#78909C', description: 'Dampers, louvers, duct fittings' },
  'Pipe Accessories': { icon: '🔩', color: '#8BC34A', description: 'Valves, fittings, flanges' },
  'Sprinklers': { icon: '💦', color: '#F44336', description: 'Fire sprinkler heads' },
  'Generic Models': { icon: '📦', color: '#9E9E9E', description: 'Generic building elements' },
  'Specialty Equipment': { icon: '🔬', color: '#673AB7', description: 'Specialized equipment' },
};

// Derived lookups
export const CATEGORY_ICONS: Record<string, string> = Object.keys(CATEGORY_DEFINITIONS).reduce(
  (acc, key) => { acc[key] = CATEGORY_DEFINITIONS[key].icon; return acc; },
  {} as Record<string, string>
);

export const CATEGORY_COLORS: Record<string, string> = Object.keys(CATEGORY_DEFINITIONS).reduce(
  (acc, key) => { acc[key] = CATEGORY_DEFINITIONS[key].color; return acc; },
  {} as Record<string, string>
);

// Derived list of known category names
export const KNOWN_CATEGORIES: string[] = Object.keys(CATEGORY_DEFINITIONS);

// Category to importance mapping used by the IFC/RVT -> POI converters
export const CATEGORY_IMPORTANCE: Record<string, number> = {
  'Structure': 0.9,
  'Architecture': 0.8,
  'MEP': 0.7,
  'Furniture': 0.5,
  'Spatial': 0.6
};

export const DEFAULT_CATEGORY_IMPORTANCE = 0.5;

// Generate a consistent color from category name for unknown categories
export const generateColorFromString = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 50%)`;
};

// Get icon based on category name keywords (fallback for unknown categories).
// 2026-07-06: THE single keyword→icon table. Seven page/viewer-local maps (LocationsPage
// getPOIIcon/getCategoryIcon, SystemsPage getSubCategoryIcon, AutodeskViewer getTypeIcon/
// getSubCategoryIcon, …) were converged onto it; their keyword concepts were folded in
// below, with THIS table's emoji winning every conflict so the same POI reads the same
// everywhere. Order matters: more specific concepts must match before generic ones.
export const getIconForCategory = (categoryName: string): string => {
  const lower = (categoryName || '').toLowerCase();
  if (lower.includes('audio') || lower.includes('visual') || lower.includes('tv') || lower.includes('display')) return '📺';
  if (lower.includes('communication') || lower.includes('phone') || lower.includes('intercom')) return '📡';
  if (lower.includes('data') || lower.includes('network')) return '🖥️';
  if (lower.includes('light') || lower.includes('lamp') || lower.includes('luminaire') || lower.includes('led')) return '💡';
  // NOTE: no bare 'panel' keyword — roof/wall/ceiling panels would misfire; electrical
  // panel categories match via 'electric'/'switchboard' or the exact-definitions table.
  if (lower.includes('electric') || lower.includes('power') || lower.includes('switchboard') || lower.includes('transformer') || lower.includes('outlet') || lower.includes('switch') || lower.includes('cable')) return '⚡';
  if (lower.includes('fire') || lower.includes('alarm') || lower.includes('smoke') || lower.includes('sprinkler') || lower.includes('extinguisher')) return '🚨';
  if (lower.includes('security') || lower.includes('camera') || lower.includes('access')) return '🔐';
  // 'stair'/'chair' must match BEFORE the hvac line — both contain the substring 'air'
  if (lower.includes('stair')) return '🪜';
  if (lower.includes('furniture') || lower.includes('chair') || lower.includes('table') || lower.includes('desk') || lower.includes('cabinet')) return '🪑';
  if (lower.includes('plumb') || lower.includes('pipe') || lower.includes('water') || lower.includes('valve') || lower.includes('faucet') || lower.includes('sink')) return '🚰';
  if (lower.includes('hvac') || lower.includes('duct') || lower.includes('air') || lower.includes('vent')) return '🌡️';
  if (lower.includes('mechanical') || lower.includes('equipment') || lower.includes('pump') || lower.includes('motor') || lower.includes('compressor') || lower.includes('fan') || lower.includes('machine')) return '⚙️';
  if (lower.includes('door')) return '🚪';
  if (lower.includes('window')) return '🪟';
  if (lower.includes('elevator') || lower.includes('lift')) return '🛗';
  if (lower.includes('column')) return '🏛️';
  if (lower.includes('roof')) return '🏠';
  if (lower.includes('wall') || lower.includes('structure') || lower.includes('beam')) return '🏗️';
  if (lower.includes('slab') || lower.includes('floor') || lower.includes('space') || lower.includes('room')) return '📐';
  if (lower.includes('building')) return '🏢';
  if (lower.includes('sensor') || lower.includes('device')) return '📡';
  return '📦';
};

// Get icon for a category: exact match first, then keyword-based fallback
export const getCategoryIcon = (categoryName: string): string => {
  if (CATEGORY_DEFINITIONS[categoryName]) {
    return CATEGORY_DEFINITIONS[categoryName].icon;
  }
  return getIconForCategory(categoryName);
};

// Get color for a category: exact match first, then generated fallback
export const getCategoryColor = (categoryName: string): string => {
  if (CATEGORY_DEFINITIONS[categoryName]) {
    return CATEGORY_DEFINITIONS[categoryName].color;
  }
  return generateColorFromString(categoryName);
};

// Get complete category info - handles both known and unknown categories
export const getCategoryInfo = (categoryName: string): CategoryDefinition => {
  if (CATEGORY_DEFINITIONS[categoryName]) {
    return CATEGORY_DEFINITIONS[categoryName];
  }

  // For unknown categories, generate styling dynamically
  return {
    icon: getIconForCategory(categoryName),
    color: generateColorFromString(categoryName),
    description: categoryName
  };
};
